import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { erroreDi } from "@/lib/crm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { dataPerEsteso } from "@/components/common/CampoData";

/**
 * L'ora di una visita in showroom, scelta a clic fra gli orari liberi della sede quel giorno.
 * Gli orari li sa solo Cal (calendario della sede, pause, prenotazioni della landing): li chiede
 * il backend. Se la sede non ha Cal (`cal: false`) resta il campo ora di prima.
 *
 * Il valore è "HH:MM" come il campo che sostituisce. Cambiare giorno, sede, cliente o visita
 * toglie l'ora scelta; un'ora che non è fra gli orari offerti (anche mentre carica o dopo un errore) si toglie
 * da sola. `ricarica` è un contatore: alzarlo rilegge gli orari (dopo un «occupato», la lista è vecchia).
 * `onCal(sì/no)` dice a chi lo usa se gli orari vengono da Cal.
 */
export default function OrariLiberi({ id, testId, giorno, sedeId, clienteId, appuntamentoId, pronto = true, ricarica = 0, valore, onScegli, onCal }) {
  const [stato, setStato] = useState({ fase: "attesa" });
  const [prova, setProva] = useState(0);
  const chiave = [giorno, sedeId, clienteId, appuntamentoId].join("|");
  const precedente = useRef(chiave);

  useEffect(() => {
    if (precedente.current !== chiave) {
      precedente.current = chiave;
      onScegli("");
    }
    if (!giorno || !pronto) {
      setStato({ fase: "attesa" });
      return undefined;
    }
    let vivo = true;
    setStato({ fase: "carico" });
    api
      .get("/calendario/orari-liberi", {
        params: { giorno, sede_id: sedeId, cliente_id: clienteId, appuntamento_id: appuntamentoId },
      })
      .then((r) => {
        if (!vivo) return;
        setStato(r.data.cal ? { fase: "ok", orari: r.data.orari } : { fase: "senza-cal" });
        onCal?.(Boolean(r.data.cal)); // chi apre il dialogo sa se la visita passa da Cal (e quindi dai WhatsApp automatici)
      })
      .catch((e) => vivo && setStato({ fase: "errore", testo: erroreDi(e) }));
    return () => {
      vivo = false;
    };
  }, [chiave, pronto, prova, ricarica]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Un'ora vale solo se è fra quelle offerte (o se il campo libero è il ripiego): in attesa, in caricamento
    // e in errore non c'è niente da scegliere, e un'ora rimasta da prima non deve partire.
    if (valore && stato.fase !== "senza-cal" && !(stato.fase === "ok" && stato.orari.includes(valore))) onScegli("");
  }, [stato, valore]); // eslint-disable-line react-hooks/exhaustive-deps

  if (stato.fase === "senza-cal")
    return (
      <div className="grid content-start gap-inside">
        <Label htmlFor={id}>Ora</Label>
        <Input id={id} type="time" step={900} value={valore} onChange={(e) => onScegli(e.target.value)} required data-testid={testId} />
      </div>
    );

  return (
    <div className="grid content-start gap-inside" data-testid={`${testId}-orari`}>
      <Label id={`${id}-etichetta`}>Ora</Label>
      {stato.fase === "ok" && stato.orari.length ? (
        <div role="group" aria-labelledby={`${id}-etichetta`} className="grid grid-cols-3 gap-inside sm:grid-cols-4 lg:grid-cols-6">
          {stato.orari.map((o) => (
            <Button
              key={o}
              type="button"
              variant={valore === o ? "default" : "outline"}
              aria-pressed={valore === o}
              onClick={() => onScegli(o)}
              data-testid={`${testId}-${o}`}
              className="h-11 tabular-nums sm:h-9"
            >
              {o}
            </Button>
          ))}
        </div>
      ) : stato.fase === "carico" ? (
        <div className="grid grid-cols-3 gap-inside sm:grid-cols-4 lg:grid-cols-6" aria-busy="true" data-testid={`${testId}-carico`}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-11 sm:h-9" />
          ))}
        </div>
      ) : stato.fase === "ok" ? (
        <p className="text-sm text-muted-foreground" data-testid={`${testId}-vuoto`}>
          Nessun orario libero il {dataPerEsteso(giorno) || giorno}: prova un altro giorno.
        </p>
      ) : stato.fase === "errore" ? (
        <div className="flex flex-wrap items-center gap-inside" data-testid={`${testId}-errore`}>
          <p role="alert" className="text-sm text-danger-foreground">
            {stato.testo}
          </p>
          <Button type="button" variant="outline" className="h-11 sm:h-9" onClick={() => setProva((n) => n + 1)}>
            Riprova
          </Button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid={`${testId}-attesa`}>
          {giorno ? "Scegli prima il cliente." : "Scegli il giorno per vedere gli orari liberi."}
        </p>
      )}
    </div>
  );
}
